import React, { useEffect, useRef, useState } from "react";
import TimeInput from "../../../../../components/TimeInput";
import DurationInput from "../../../../../components/DurationInput";
import IconButton from "../../../../../components/IconButton";
import SaveDot, { SaveState } from "../../../../../components/SaveDot";
import {
  CopyIcon,
  EditIcon,
  TrashIcon,
} from "../../../../../components/IconButton/icons";
import {
  addHoursToTime,
  displayTime,
  formatHoursShort,
  formatTime,
  hoursBetween,
  LunchBreak,
  subtractHoursFromTime,
} from "../../../../../utils/timesheetTime";
import { TimesheetEntry, TimesheetEntryPatch } from "../../../config";
import { entryLabel } from "../../../timesheetDerive";

export interface DayEntryRowProps {
  entry: TimesheetEntry;
  overlaps: boolean;
  isReadOnly: boolean;
  lunch: LunchBreak;
  saveState: (field: string) => SaveState;
  onPatch: (
    entry: TimesheetEntry,
    field: string,
    patch: TimesheetEntryPatch,
  ) => void;
  onEdit: (entry: TimesheetEntry) => void;
  onDuplicate: (entry: TimesheetEntry) => void;
  onDelete: (entry: TimesheetEntry) => void;
  onProjectClick?: (entry: TimesheetEntry) => void;
}

/**
 * One entry in the Day view, editable field by field. Each field commits on
 * its own and shows its own save dot; the row's leading dot is the worst of
 * them. Changing a start or an end (with both present) recomputes the hours;
 * changing the hours of a timed entry moves its end.
 */
const DayEntryRow: React.FC<DayEntryRowProps> = ({
  entry,
  overlaps,
  isReadOnly,
  lunch,
  saveState,
  onPatch,
  onEdit,
  onDuplicate,
  onDelete,
  onProjectClick,
}) => {
  const label = entryLabel(entry);
  const start = entry.start_time ? formatTime(entry.start_time) : null;
  const end = entry.end_time ? formatTime(entry.end_time) : null;
  const hours = Number(entry.hours);

  // Notes are a plain text field committed on blur; local text so a refetch
  // does not overwrite what is being typed.
  const [notes, setNotes] = useState(entry.description ?? "");
  const notesFocused = useRef(false);
  useEffect(() => {
    if (!notesFocused.current) setNotes(entry.description ?? "");
  }, [entry.description]);

  // A commit the row refuses (an end before the start, cleared hours) is
  // said out loud under the row, and the field is remounted so it shows the
  // saved value again instead of the refused text.
  const [refusal, setRefusal] = useState<string | null>(null);
  const [restoreKey, setRestoreKey] = useState(0);
  const refuse = (message: string) => {
    setRefusal(message);
    setRestoreKey((k) => k + 1);
  };
  const accept = () => {
    if (refusal) setRefusal(null);
  };

  const states: SaveState[] = ["start", "end", "hours", "description"].map(
    saveState,
  );
  const rowState: SaveState = states.includes("error")
    ? "error"
    : states.includes("saving")
      ? "saving"
      : states.includes("saved")
        ? "saved"
        : "idle";

  const commitStart = (next: string | null) => {
    accept();
    if (next === null) {
      // Clearing either time clears both; the hours stay.
      onPatch(entry, "start", { start_time: null, end_time: null });
      return;
    }
    if (end) {
      const h = hoursBetween(next, end, lunch);
      if (!h) {
        // Start moved past the end: keep the duration, move the end.
        onPatch(entry, "start", {
          start_time: next,
          end_time: addHoursToTime(next, hours, lunch),
          hours,
        });
        return;
      }
      onPatch(entry, "start", { start_time: next, end_time: end, hours: h });
      return;
    }
    // A start on an hours-only entry: the end follows from the hours.
    onPatch(entry, "start", {
      start_time: next,
      end_time: addHoursToTime(next, hours, lunch),
      hours,
    });
  };

  const commitEnd = (next: string | null) => {
    if (next === null) {
      accept();
      onPatch(entry, "end", { start_time: null, end_time: null });
      return;
    }
    if (start) {
      const h = hoursBetween(start, next, lunch);
      if (!h) {
        refuse(
          `The end must be after the ${displayTime(start)} start (the lunch break is not worked).`,
        );
        return;
      }
      accept();
      onPatch(entry, "end", { start_time: start, end_time: next, hours: h });
      return;
    }
    // An end on an hours-only entry: the start follows from the hours.
    accept();
    onPatch(entry, "end", {
      start_time: subtractHoursFromTime(next, hours, lunch),
      end_time: next,
      hours,
    });
  };

  const commitHours = (next: number | null) => {
    if (!next) {
      refuse(
        "An entry needs some hours. Delete it with the bin if it is not wanted.",
      );
      return;
    }
    accept();
    if (start) {
      onPatch(entry, "hours", {
        start_time: start,
        end_time: addHoursToTime(start, next, lunch),
        hours: next,
      });
      return;
    }
    onPatch(entry, "hours", { hours: next });
  };

  const commitNotes = () => {
    const trimmed = notes.trim();
    if ((entry.description ?? "") === trimmed) return;
    onPatch(entry, "description", { description: trimmed || null });
  };

  return (
    <>
      <tr
        className={`day-view__row ${entry.leave_type ? "is-leave" : ""} ${overlaps ? "is-overlap" : ""}`}
      >
        <td className="day-view__dot-col">
          {rowState !== "idle" && <SaveDot state={rowState} />}
          {overlaps && rowState === "idle" && (
            <span
              className="day-view__overlap"
              title="Overlaps the entry above"
            >
              !
            </span>
          )}
        </td>
        <td className="day-view__time-col">
          {isReadOnly ? (
            <span className="ui-table__mono">
              {start ? displayTime(start) : "–"}
            </span>
          ) : (
            <TimeInput
              key={restoreKey}
              value={start}
              onCommit={commitStart}
              aria-label="Start time"
              invalid={saveState("start") === "error"}
            />
          )}
        </td>
        <td className="day-view__time-col">
          {isReadOnly ? (
            <span className="ui-table__mono">
              {end ? displayTime(end) : "–"}
            </span>
          ) : (
            <TimeInput
              key={restoreKey}
              value={end}
              onCommit={commitEnd}
              aria-label="End time"
              invalid={saveState("end") === "error"}
            />
          )}
        </td>
        <td className="day-view__hours-col">
          {isReadOnly ? (
            <span className="day-view__hours">{formatHoursShort(hours)}</span>
          ) : (
            <DurationInput
              key={restoreKey}
              value={hours}
              onCommit={commitHours}
              aria-label="Hours"
              invalid={saveState("hours") === "error"}
            />
          )}
        </td>
        <td className="day-view__project-col">
          {entry.leave_type ? (
            <span className="day-view__project">
              <span className="ui-chip">Leave</span>
              <span className="day-view__project-name">{label.name}</span>
            </span>
          ) : onProjectClick ? (
            <button
              type="button"
              className="day-view__project day-view__project--btn"
              onClick={() => onProjectClick(entry)}
            >
              <span className="ui-table__mono">{label.code}</span>
              <span className="day-view__project-name">{label.name}</span>
              {label.sub && (
                <span className="day-view__project-sub">{label.sub}</span>
              )}
            </button>
          ) : (
            <span className="day-view__project">
              <span className="ui-table__mono">{label.code}</span>
              <span className="day-view__project-name">{label.name}</span>
              {label.sub && (
                <span className="day-view__project-sub">{label.sub}</span>
              )}
            </span>
          )}
        </td>
        <td className="day-view__notes-col">
          {isReadOnly ? (
            entry.description ? (
              <span>{entry.description}</span>
            ) : (
              <span className="ui-table__na">–</span>
            )
          ) : (
            <input
              type="text"
              className={`text-input text-input--sm day-view__notes ${saveState("description") === "error" ? "is-invalid" : ""}`}
              value={notes}
              placeholder="Notes"
              aria-label="Notes"
              onFocus={() => {
                notesFocused.current = true;
              }}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => {
                notesFocused.current = false;
                commitNotes();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setNotes(entry.description ?? "");
                  e.currentTarget.blur();
                }
              }}
            />
          )}
        </td>
        {!isReadOnly && (
          <td className="ui-table__actions">
            <div className="row-actions">
              <IconButton
                icon={<EditIcon size={14} />}
                label="Edit entry"
                size="sm"
                onClick={() => onEdit(entry)}
              />
              <IconButton
                icon={<CopyIcon size={14} />}
                label="Duplicate entry"
                size="sm"
                onClick={() => onDuplicate(entry)}
              />
              <IconButton
                icon={<TrashIcon size={14} />}
                label="Delete entry"
                size="sm"
                tone="danger"
                onClick={() => onDelete(entry)}
              />
            </div>
          </td>
        )}
      </tr>
      {refusal && (
        <tr className="day-view__row-error">
          <td colSpan={7}>
            <p className="field__error" role="alert">
              {refusal}
            </p>
          </td>
        </tr>
      )}
    </>
  );
};

export default DayEntryRow;
